import React, { useEffect, useRef } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

/*
 * Every create/edit dialog in the portal used to hand-roll the same
 * "fixed inset-0 bg-gray-600 bg-opacity-50 ... relative top-20 mx-auto"
 * markup, with no ESC key, no click-outside, and no shared width scale.
 * This is the one place that logic lives now.
 */

export type ModalSize = 'sm' | 'md' | 'lg';

const SIZE_STYLES: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-2xl',
};

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  size?: ModalSize;
  children: React.ReactNode;
  footer?: React.ReactNode;
}

const Modal: React.FC<ModalProps> = ({ open, onClose, title, size = 'sm', children, footer }) => {
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-gray-900/50 px-4 py-10 animate-fade-in"
      onClick={(e) => {
        if (e.target === overlayRef.current) onClose();
      }}
    >
      <div role="dialog" aria-modal="true" aria-label={title} className={`w-full ${SIZE_STYLES[size]} rounded-xl bg-white shadow-pop animate-slide-up dark:bg-gray-800`}>
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4 dark:border-gray-700">
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="px-5 py-4 text-gray-900 dark:text-gray-100">{children}</div>
        {footer && <div className="flex justify-end gap-3 border-t border-gray-100 px-5 py-4 dark:border-gray-700">{footer}</div>}
      </div>
    </div>
  );
};

export default Modal;
